import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { flattenTree } from 'components/HierarchyViewSelect/HierarchyViewSelect';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type {
    WorkOrderChangeObjectItem,
    WorkOrderChangePriceItem,
    WorkOrderObjectData,
    WorkOrderPriceItem,
    WorkOrderSeasonData,
} from 'gql/graphql';
import { groupByParentId } from 'pages/Project/Common/WorkOrderStructure/types';
import { useSeasons } from 'pages/Project/UerProject/WorkOrder/hooks/useSeasons';
import { useWorkOrderObjects } from 'pages/Project/UerProject/WorkOrder/hooks/useWorkOrderObjects';
import { useOrderSummaryTableColumns } from 'pages/Project/UerProject/WorkOrder/WorkOrderSummaryTableNew/hooks';
import { useWorkOrderChangeObjects, useWorkOrderChangeSeasons } from 'pages/Project/UerProject/WorkOrderChange/hooks';

import { useCallback, useState, useMemo } from 'react';
import type React from 'react';
import { useParams } from 'react-router-dom';

import { useUerWorkOrderObjectTotals } from './hooks/useUerWorkOrderObjectTotals';
import { useUerWorkOrderChangeObjectTotals } from './hooks/useWorkOrderChangeObjectTotals';
import { useWorkOrderObjectTotalColumns } from './hooks/useWorkOrderObjectTotalColumns';

import Style from './WorkOrderObjectTotals.module.css';

interface IWorkOrderObjectTotals {
    onClose: () => void;
    rowData: WorkOrderPriceItem | WorkOrderChangePriceItem | null | undefined;
    costRounding: number;
    isChange?: boolean;
}

const getRowId = ({ data }: { data: WorkOrderObjectData | WorkOrderChangeObjectItem }) => String(data.id);

export const WorkOrderOrChangeObjectTotals: React.FC<IWorkOrderObjectTotals> = (props) => {
    const { orderId, orderChangeId } = useParams();
    const { onClose, rowData, costRounding, isChange } = props;

    const workOrderId = Number(orderId);
    const workOrderChangeId = Number(orderChangeId);

    const [workOrderSeasons] = useSeasons(workOrderId, false);

    const { seasons: workOrderChangeSeasons } = useWorkOrderChangeSeasons(workOrderChangeId, false);

    const [workOrderObject, setWorkOrderObject] = useState<WorkOrderObjectData | WorkOrderChangeObjectItem>();

    const title = useMemo(() => {
        if (!workOrderObject) {
            return 'Итоги объекта и подобъектов строительства';
        }

        return `Итоги ${workOrderObject.parentId ? 'подобъекта' : 'объекта'} строительства «${workOrderObject.name}»`;
    }, [workOrderObject]);

    const { patchedObjectForWorkOrderFilter } = useWorkOrderObjects(workOrderId, false);

    const { workOrderChangeObjectsDataPatched } = useWorkOrderChangeObjects({
        workOrderChangeId,
        withEffect: false,
        resetOnUnmount: false,
    });

    const actualItems: WorkOrderObjectData[] | WorkOrderChangeObjectItem[] = useMemo(() => {
        const items = (
            isChange ? workOrderChangeObjectsDataPatched || [] : patchedObjectForWorkOrderFilter || []
        ) as any[];

        const id = isChange ? rowData?.entryId : rowData?.itemId;

        return [items.find((i) => i.id === id), ...(flattenTree(groupByParentId(items) as any, String(id)) as any[])];
    }, [isChange, patchedObjectForWorkOrderFilter, rowData, workOrderChangeObjectsDataPatched]);

    const { uerWorkOrderObjectTotalsData, uerWorkOrderObjectTotalsStatus, uerWorkOrderObjectTotalsMakeRequest } =
        useUerWorkOrderObjectTotals(workOrderId);

    const {
        uerWorkOrderChangeObjectTotalsData,
        uerWorkOrderChangeObjectTotalsStatus,
        uerWorkOrderChangeObjectTotalsMakeRequest,
    } = useUerWorkOrderChangeObjectTotals(workOrderChangeId);

    const detailsItems = useMemo(
        () =>
            (isChange
                ? uerWorkOrderChangeObjectTotalsData?.uerWorkOrderChangeObjectTotals?.data
                : uerWorkOrderObjectTotalsData?.uerWorkOrderObjectTotals?.data
            )?.map((i, id) => ({ ...i, id })) || [],
        [
            isChange,
            uerWorkOrderChangeObjectTotalsData?.uerWorkOrderChangeObjectTotals?.data,
            uerWorkOrderObjectTotalsData?.uerWorkOrderObjectTotals?.data,
        ],
    );

    const onExpandDetailsClick = useCallback(
        (data: WorkOrderObjectData | WorkOrderChangeObjectItem) => {
            setWorkOrderObject(data);

            if (isChange) {
                const { workOrderObjectId, workOrderChangeObjectId } = data as WorkOrderChangeObjectItem;

                uerWorkOrderChangeObjectTotalsMakeRequest(workOrderObjectId, workOrderChangeObjectId);
            } else {
                uerWorkOrderObjectTotalsMakeRequest(data.id);
            }
        },
        [isChange, uerWorkOrderChangeObjectTotalsMakeRequest, uerWorkOrderObjectTotalsMakeRequest],
    );

    const onCloseLocal = useCallback(() => {
        if (workOrderObject) {
            setWorkOrderObject(undefined);
        } else {
            onClose();
        }
    }, [onClose, workOrderObject]);

    const columns = useWorkOrderObjectTotalColumns({ onExpandDetailsClick, actualItems });

    const { columns: detailsColumns } = useOrderSummaryTableColumns({
        instanceId: isChange ? orderChangeId : orderId,
        costRounding,
        workOrderSeasons: (isChange ? workOrderChangeSeasons : workOrderSeasons) as WorkOrderSeasonData[],
    });

    const isLoading = useMemo(
        () =>
            uerWorkOrderChangeObjectTotalsStatus === RequestStatus.FETCHING ||
            uerWorkOrderObjectTotalsStatus === RequestStatus.FETCHING,
        [uerWorkOrderChangeObjectTotalsStatus, uerWorkOrderObjectTotalsStatus],
    );

    return (
        <ModalDialog
            isOpen
            title={title}
            onClose={onCloseLocal}
            dockedToWindow="bottom"
            withTitleCloseButton
            isOnClickOutsideDisable
            withBottomButtons={false}
            isLoading={isLoading}
        >
            <div className={workOrderObject ? Style.workOrderObjectsTotalDetails : Style.workOrderObjectsTotal}>
                <AgGridTable
                    columnDefs={(workOrderObject ? detailsColumns : columns) as any}
                    rowData={(workOrderObject ? detailsItems : actualItems) as any}
                    getRowId={getRowId}
                />
            </div>
        </ModalDialog>
    );
};

WorkOrderOrChangeObjectTotals.displayName = 'WorkOrderObjectTotals';
